import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { HasPermission } from "../../components/AccessControl/ProtectedComponent";
import StatusBadge from "../UI/StatusBadge";

const CandidateDetails = ({
  interview,
  candidate,
  onUpdateStatus,
  onClose,
  onSelectClick,
  onRejectClick,
}) => {
  const { user } = useAuth();

  // Helper function to get the current round for an interview
  const getCurrentRound = (interview) => {
    if (!interview?.rounds || !interview.current_round) {
      return null;
    }
    return (
      interview.rounds.find(
        (round) => round.round_number === interview.current_round,
      ) || null
    );
  };

  // Check if logged-in user is assigned to the current round
  const isUserAssignedToRound = (interview) => {
    if (!user?.id || !interview) return false;
    const currentRound = getCurrentRound(interview);
    return currentRound?.assigned_to?.id === user.id;
  };

  // Get current round status
  const getRoundStatus = () => {
    const currentRound = getCurrentRound(interview);
    return currentRound?.status;
  };

  // Determine if Select/Reject buttons should be shown
  const shouldShowSelectReject = () => {
    const roundStatus = getRoundStatus();
    const userAssigned = isUserAssignedToRound(interview);
    return roundStatus === "Scheduled" && userAssigned;
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [feedback, setFeedback] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");

  // // console.log("onUpdateStatus: ", onUpdateStatus);

  const handleOpenModal = (status) => {
    setSelectedStatus(status);
    setIsModalOpen(true);
    setFeedback("");
    setFeedbackError("");
  };

  const handleSubmitFeedback = () => {
    if (!feedback.trim()) {
      setFeedbackError("Feedback is required.");
      return;
    }

    setFeedbackError("");

    // Send status and feedback to parent
    if (onUpdateStatus) {
      onUpdateStatus({
        status: selectedStatus,
        overallFeedback: feedback.trim(),
      });
    }

    setFeedback("");
    setIsModalOpen(false);
  };

  const handleViewDocument = (url, title) => {
    try {
      if (url) {
        setDocumentUrl(url);
        setDocumentTitle(title || "Document Preview");
        setIsDocumentModalOpen(true);
      }
    } catch (error) {
      console.error("Error while opening the document:", error);
      alert(
        "An error occurred while trying to open the document. Please try again.",
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      // Format time in 12-hour format with AM/PM
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const formattedHours = String(hours).padStart(2, "0");

      return `${day}/${month}/${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const renderContent = () => {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h4 className="text-gray-500 text-sm font-medium mb-1">Email</h4>
            <p className="text-gray-900 font-medium">{candidate.email}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h4 className="text-gray-500 text-sm font-medium mb-1">
              Campaign Name
            </h4>
            <p className="text-gray-900 font-medium">
              {candidate.campaign_name}
            </p>
          </div>
        </div>

        {/* Current Round Information Section */}
        {(() => {
          const currentRound = getCurrentRound(interview);
          if (!currentRound) return null;

          const roundStatus = getRoundStatus();
          const scheduledAt = currentRound.scheduled_at;
          const interviewType = currentRound.interview_type;
          const interviewLink = currentRound.interview_link;
          const location = currentRound.location;
          const totalRounds =
            interview?.total_rounds ?? interview?.interview?.total_rounds;
          const currentRoundNumber =
            interview?.current_round ?? interview?.interview?.current_round;
          const expectedRoundType =
            interview?.expected_round_type ??
            interview?.interview?.expected_round_type;

          return (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <h4 className="text-gray-500 text-sm font-medium mb-3">
                Current Round Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="text-gray-500 text-xs font-medium mb-1">
                    Total Rounds
                  </h5>
                  <p className="text-gray-900 font-medium">
                    {totalRounds || "N/A"}
                  </p>
                </div>
                <div>
                  <h5 className="text-gray-500 text-xs font-medium mb-1">
                    Current Round
                  </h5>
                  <p className="text-gray-900 font-medium">
                    {currentRoundNumber
                      ? expectedRoundType
                        ? `${currentRoundNumber} (${expectedRoundType})`
                        : currentRoundNumber
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <h5 className="text-gray-500 text-xs font-medium mb-1">
                    Round Status
                  </h5>
                  <div className="mt-1">
                    <StatusBadge status={roundStatus || "N/A"} />
                  </div>
                </div>
                <div>
                  <h5 className="text-gray-500 text-xs font-medium mb-1">
                    Scheduled At
                  </h5>
                  <p className="text-gray-900 font-medium">
                    {scheduledAt ? formatDate(scheduledAt) : "N/A"}
                  </p>
                </div>
                <div>
                  <h5 className="text-gray-500 text-xs font-medium mb-1">
                    Interview Type
                  </h5>
                  <p className="text-gray-900 font-medium capitalize">
                    {interviewType || "N/A"}
                  </p>
                </div>
                <div>
                  {interviewType === "virtual" ? (
                    <>
                      <h5 className="text-gray-500 text-xs font-medium mb-1">
                        Interview Link
                      </h5>
                      {interviewLink ? (
                        <a
                          href={interviewLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium break-all"
                        >
                          {interviewLink}
                        </a>
                      ) : (
                        <p className="text-gray-900 font-medium">N/A</p>
                      )}
                    </>
                  ) : interviewType === "physical" ? (
                    <>
                      <h5 className="text-gray-500 text-xs font-medium mb-1">
                        Location
                      </h5>
                      <p className="text-gray-900 font-medium">
                        {location || "N/A"}
                      </p>
                    </>
                  ) : (
                    <>
                      <h5 className="text-gray-500 text-xs font-medium mb-1">
                        Interview Details
                      </h5>
                      <p className="text-gray-900 font-medium">N/A</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
        {/* Feedback Section */}
        {(interview?.feedback || interview?.overall_feedback) && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h4 className="text-gray-500 text-sm font-medium mb-2">
              Interview Feedback
            </h4>
            <p className="text-gray-900 font-medium whitespace-pre-wrap">
              {interview?.feedback || interview?.overall_feedback}
            </p>
          </div>
        )}

        {/* Resume and Offer Letter Section - Combined */}
        {(candidate.resume_path ||
          interview?.resume_url ||
          interview?.offer_letter?.document_url ||
          interview?.offer_letter?.signed_document_url ||
          candidate?.offer_letter?.document_path ||
          candidate?.offer_letter?.signed_offer_letter_path) && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h4 className="text-gray-500 text-sm font-medium mb-3">
              Documents
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Resume Section */}
              {(candidate.resume_path || interview?.resume_url) && (
                <div>
                  <h5 className="text-gray-500 text-xs font-medium mb-2">
                    Resume
                  </h5>
                  <button
                    onClick={() =>
                      handleViewDocument(
                        interview?.resume_url ||
                          `https://hrmspayroll.prepstripe.com/storage/${candidate.resume_path}`,
                        "Resume",
                      )
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-indigo-600 rounded-lg hover:bg-gray-50 transition-all focus:ring-2 focus:ring-indigo-200"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                        clipRule="evenodd"
                      />
                    </svg>
                    View Resume
                  </button>
                </div>
              )}

              {/* Offer Letter Section */}
              {(interview?.offer_letter?.document_url ||
                interview?.offer_letter?.signed_document_url ||
                candidate?.offer_letter?.document_path ||
                candidate?.offer_letter?.signed_offer_letter_path) && (
                <div>
                  <h5 className="text-gray-500 text-xs font-medium mb-2">
                    Offer Letter Documents
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {(interview?.offer_letter?.document_url ||
                      candidate?.offer_letter?.document_path) && (
                      <button
                        onClick={() =>
                          handleViewDocument(
                            interview?.offer_letter?.document_url ||
                              `https://hrmspayroll.prepstripe.com/storage/${candidate?.offer_letter?.document_path}`,
                            "Offer Letter",
                          )
                        }
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-indigo-600 rounded-lg hover:bg-gray-50 transition-all focus:ring-2 focus:ring-indigo-200"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        View Offer Letter
                      </button>
                    )}
                    {(interview?.offer_letter?.signed_document_url ||
                      candidate?.offer_letter?.signed_offer_letter_path) && (
                      <button
                        onClick={() =>
                          handleViewDocument(
                            interview?.offer_letter?.signed_document_url ||
                              `https://hrmspayroll.prepstripe.com/storage/${candidate?.offer_letter?.signed_offer_letter_path}`,
                            "Signed Offer Letter",
                          )
                        }
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-indigo-600 rounded-lg hover:bg-gray-50 transition-all focus:ring-2 focus:ring-indigo-200"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        View Signed Offer Letter
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rejection Reason Section */}
        {(interview?.offer_letter?.offer_rejection_reason ||
          candidate?.offer_letter?.offer_rejection_reason) && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h4 className="text-red-600 text-sm font-medium mb-1">
              Rejection Reason
            </h4>
            <p className="text-gray-900 font-medium whitespace-pre-wrap">
              {interview?.offer_letter?.offer_rejection_reason ||
                candidate?.offer_letter?.offer_rejection_reason}
            </p>
          </div>
        )}
        {/* Select/Reject Buttons */}
        {shouldShowSelectReject() && (
          <div className="flex gap-4 pt-4 border-t border-gray-100">
            <HasPermission permission="interviews_update">
              <button
                onClick={() => handleOpenModal("Selected")}
                className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all shadow-sm flex items-center justify-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Select Candidate
              </button>
            </HasPermission>
            <HasPermission permission="interviews_update">
              <button
                onClick={() => handleOpenModal("Rejected")}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all shadow-sm flex items-center justify-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Reject Candidate
              </button>
            </HasPermission>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-50 p-6 rounded-2xl">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 mb-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-2 items-center">
            <div className="h-16 w-16 rounded-full bg-gradient-to-r from-indigo-600 to-blue-400 flex items-center justify-center text-2xl font-bold text-white shadow-md">
              {candidate.name.charAt(0).toUpperCase()}
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900">
                {candidate.name}
              </h3>
              <div className="mt-1">
                <StatusBadge status={candidate.candidate_status} />
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="block md:hidden lg:hidden text-gray-500 dark:hover:text-gray-400 hover:text-gray-800 text-xl m-2"
          >
            ✖
          </button>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="hidden md:block lg:block text-gray-500 dark:hover:text-gray-400 hover:text-gray-800 text-xl m-2"
          >
            ✖
          </button>
        </div>
      </div>

      {/* Content */}
      <div>{renderContent()}</div>

      {/* Feedback Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-40 backdrop-blur-sm">
          <div
            className="bg-white w-full h-full md:rounded-none rounded-xl shadow-xl "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    selectedStatus === "Selected"
                      ? "bg-emerald-500"
                      : selectedStatus === "Rejected"
                        ? "bg-red-500"
                        : "bg-amber-500"
                  }`}
                ></div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Provide Feedback ({selectedStatus})
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setFeedback("");
                  setFeedbackError("");
                }}
                className="text-gray-400 hover:text-gray-600 focus:outline-none h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              >
                &times;
              </button>
            </div>
            <div className="px-6 py-5">
              <textarea
                rows="6"
                value={feedback}
                required
                onChange={(e) => {
                  setFeedback(e.target.value);
                  setFeedbackError("");
                }}
                placeholder="Write detailed feedback about this candidate..."
                className="w-full border border-gray-200 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              ></textarea>
              {feedbackError && (
                <p className="text-red-500 text-sm mt-1">{feedbackError}</p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end items-center gap-3">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setFeedback("");
                  setFeedbackError("");
                }}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitFeedback}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow-sm"
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {isDocumentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-[95%] md:w-[90%] h-[100%] relative flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {documentTitle}
              </h2>
              <button
                onClick={() => {
                  setIsDocumentModalOpen(false);
                  setDocumentUrl("");
                  setDocumentTitle("");
                }}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-2xl font-bold"
              >
                &times;
              </button>
            </div>
            {/* Body */}
            <div className="flex-1 overflow-hidden">
              <iframe
                src={documentUrl}
                title={documentTitle}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDetails;
